import React, { useCallback, useEffect } from 'react'
import { useTranslation, Trans, TransProps } from 'react-i18next'
import { TOptions } from 'i18next'
import { TranslationKey } from './types'
import { UserService } from '@duck/api-definitions'

// Type for the translation function
export type TranslateFunction = <K extends TranslationKey>(
    key: K,
    options?: TOptions
) => string

const RTL_LANGUAGES: readonly UserService.UserSettingsGuiLanguageEnum[] = ['he']

export function isRtl(lng: string): boolean {
    const base = (lng.split('-')[0] ?? lng) as UserService.UserSettingsGuiLanguageEnum
    return (RTL_LANGUAGES as readonly string[]).includes(base)
}

export function applyDocumentDirection(lng: string): void {
    if (typeof document === 'undefined') return
    const dir = isRtl(lng) ? 'rtl' : 'ltr'
    document.documentElement.setAttribute('dir', dir)
    document.documentElement.setAttribute('lang', lng)
}

/**
 * Type-safe Trans component props
 * Constraints i18nKey to valid TranslationKey values
 */
export interface TypedTransProps extends Omit<TransProps<string>, 'i18nKey'> {
    i18nKey?: TranslationKey | TranslationKey[]
}

/**
 * Type-safe translation hook
 * Provides compile-time type checking for translation keys
 */
export function useTar() {
    const { t, i18n } = useTranslation()

    /**
     * Type-safe translation function
     * @param key - Translation key in dot notation (e.g., 'common.welcome')
     * @param options - Optional interpolation options
     * @returns Translated string
     */
    const translate = useCallback(
        <K extends TranslationKey>(key: K, options?: TOptions): string => {
            return t(key, options) as string
        },
        [t]
    )

    /**
     * Change the current language
     * Applies RTL to the document (and Tailwind) when lng is in RTL_LANGUAGES.
     * @param lng - Language code (e.g., 'en', 'he')
     */
    const changeLanguage = (lng: string) => {
        return i18n.changeLanguage(lng).then(() => {
            applyDocumentDirection(lng)
        })
    }

    /**
     * Check if a language is available
     * @param lng - Language code to check
     */
    const hasLanguage = (lng: string) => {
        return i18n.hasResourceBundle(lng, 'translation')
    }

    const language = i18n.resolvedLanguage ?? i18n.language
    const locale = language?.startsWith('he') ? 'he-IL' : 'en-US'

    return {
        t: translate,
        changeLanguage,
        hasLanguage,
        language,
        locale,
        isRtl: isRtl(i18n.language),
        i18n,
    }
}

/**
 * Syncs document dir and lang with current i18n language (for RTL and Tailwind).
 * Mount once at app root so direction is correct on load and when language changes.
 */
export function DirectionSync() {
    const { i18n } = useTranslation()
    useEffect(() => {
        applyDocumentDirection(i18n.language)
    }, [i18n.language])
    return null
}

/**
 * Type-safe Trans component
 * Provides compile-time type checking for translation keys with rich text formatting
 * 
 * @example
 * ```tsx
 * import { TypedTrans } from '@modules/i18n';
 * 
 * // Type-safe usage with components
 * <TypedTrans
 *   i18nKey="common.iAgreeToTheXandY"
 *   components={[
 *     <Link key="terms" to="/terms" />,
 *     <Link key="privacy" to="/privacy" />
 *   ]}
 * />
 * ```
 */
export const TypedTrans: React.FC<TypedTransProps> = (props) => {
    return <Trans {...props} />
}

export default useTar

